import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, CUSTOM_ELEMENTS_SCHEMA, OnInit, inject } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { getAthleteAddressInfoPath, getAthletePath, OwnerCenterInfo } from '@libs/entity-lib';
import { Store } from '@ngrx/store';
import { setOwnerInfo } from '../../core/store/profile/profile.actions';

import { map, take } from 'rxjs';
import * as fromSportCenterActions from '../../core/store/profile/profile.actions';
import { selectIsEmailValid, selectOwnerInfo, selectSportCenterLoading } from '../../core/store/profile/profile.selectors';
import { UtilService } from '../../core/utils/utils.service';

import { CommonModule } from '@angular/common';

import { InputMaskLibModule } from '@libs/input-mask';
import { TranslationLibModule } from '@libs/translation';
import { cpfMaskitoOptions, cpfPredicate, phoneMaskitOptions } from '@libs/util-ui';
import { MaskitoDirective } from '@maskito/angular';
import { MaskitoElementPredicate, MaskitoOptions } from '@maskito/core';
import { CoreModule } from '../../core/core.module';

import { IonBackButton, IonButton, IonButtons, IonCol, IonContent, IonDatetime, IonFooter, IonGrid, IonHeader, IonIcon, IonInput, IonItem, IonPopover, IonRow, IonSpinner, IonTitle, IonToolbar } from '@ionic/angular/standalone';

@Component({
    selector: 'crud-athlete-info',
    templateUrl: 'athlete-info.component.html',
    styleUrls: ['athlete-info.component.scss'],
    imports: [
    IonBackButton,
    IonButton,
    IonButtons,
    IonCol,
    IonContent,
    IonDatetime,
    IonFooter,
    IonGrid,
    IonHeader,
    IonIcon,
    IonInput,
    IonItem,
    IonPopover,
    IonRow,
    IonSpinner,
    IonTitle,
    IonToolbar,
    CommonModule,
    ReactiveFormsModule,
    TranslationLibModule,
    InputMaskLibModule,
    CoreModule,
    MaskitoDirective
  ],
    providers: [
        DatePipe,
        UtilService
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AthleteInfoComponent implements OnInit {
  private store = inject(Store);
  private router = inject(Router);
  private datePipe = inject(DatePipe);
  private utilService = inject(UtilService);
  private formBuilder = inject(FormBuilder);

  form: FormGroup;

  isLoading$ = this.store.select(selectSportCenterLoading);
  selectOwnerInfo$ = this.store.select(selectOwnerInfo);
  isEmailValid$ = this.store.select(selectIsEmailValid);

  readonly phoneMaskitoOptions: MaskitoOptions = phoneMaskitOptions;
  readonly phonePredicate: MaskitoElementPredicate = async (el: any) =>
    (el as any).getInputElement();

  readonly cpfMaskitoOptions = cpfMaskitoOptions;
  readonly cpfPredicate = cpfPredicate;

  constructor() {
    this.createForm();
  }

  createForm() {
    this.form = this.formBuilder.group({
      fullName: ['', [Validators.required, Validators.minLength(3)]],
      cpf: ['', [Validators.required, this.utilService.cpfValidator]],
      birthDate: ['', Validators.required],
      cellphone: ['', [Validators.required]],
      email: new FormControl('', [Validators.required, Validators.email])
    });
  }

  ngOnInit(): void {
      this.selectOwnerInfo$.pipe(
        take(1),
        map((ownerInfo) => {
          if (ownerInfo) {
            this.form.patchValue(ownerInfo);
          }
        })
      ).subscribe();
    }

  isCPFInvalid(): boolean {
    const cpfValue = this.form.get('cpf')?.value;
    const invalidCPFFormat = this.form.get('cpf')?.errors?.['invalidCpf'];
    return this.utilService.isCPFInvalid(cpfValue, invalidCPFFormat);
  }

  gotNextPage() {
    if (this.form.valid) {
      const ownerCenterInfoRawValue = this.form.getRawValue() as OwnerCenterInfo;
      const ownerCenterInfo = {
        ...ownerCenterInfoRawValue,
        birthDate: this.getFormattedDate(ownerCenterInfoRawValue.birthDate) ?? ''
      };
      this.store.dispatch(setOwnerInfo({ ownerCenterInfo }));
      this.router.navigate([getAthleteAddressInfoPath()]);
    } else {
      this.markAsDirty();
    }
  }

  getFormattedDate(dateString: string): string | null {
    const date = new Date(dateString);
    return this.datePipe.transform(date, 'yyyy-MM-dd');
  }

  onEmailChange(): void {
    const isValid = this.form.get('email')?.valid;
    if (isValid) {
      const email = this.getEmailFromForm();
      this.store.dispatch(fromSportCenterActions.verifyEmail({ email }));
    }
  }

  markAsDirty() {
    Object.keys(this.form.controls).forEach((key) => {
      const control = this.form.get(key);
      if (control) {
        control.markAsTouched();
        control.markAsDirty();
      }
    });
  }

  getDefaultHref(): string {
    return getAthletePath();
  }

  getLocale() {
    return this.utilService.getLocale();
  }

  private getEmailFromForm() {
    return this.form.get('email')?.value?.trim() ?? '';
  }
}
